import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  CardContent
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Warning as ToxicityIcon,
  Visibility as ViewIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useUserData } from '../../hooks/useUserData';
import { getAllToxicityRecords } from '../../services/chemotherapyService';
import { CTCAE_GRADES, TOXICITY_CATEGORIES, CTCAE_GRADE_COLORS } from '../../types/enums';
import { PageHeader, ContentCard, HeaderButton } from './oncologyStyles';

const ToxicityList = () => {
  const navigate = useNavigate();
  const { defaultFacility } = useUserData();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Search/Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const fetchRecords = useCallback(async () => {
    if (!defaultFacility?.id) return;

    try {
      setLoading(true);
      setError(null);
      const result = await getAllToxicityRecords(defaultFacility.id, {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm,
        grade: gradeFilter,
        category: categoryFilter
      });
      setRecords(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error('Error fetching toxicity records:', err);
      setError('Failed to load toxicity records');
    } finally {
      setLoading(false);
    }
  }, [defaultFacility?.id, page, rowsPerPage, searchTerm, gradeFilter, categoryFilter]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleSearch = () => {
    setPage(0);
    fetchRecords();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setGradeFilter('');
    setCategoryFilter('');
    setPage(0);
  };

  const handleViewRecord = (record) => {
    navigate(`/oncology/toxicity/${record.id}`);
  };

  const getGradeColor = (grade) => {
    const colorMap = {
      '1': 'success',
      '2': 'info',
      '3': 'warning',
      '4': 'error',
      '5': 'error'
    };
    return colorMap[grade] || 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box>
      <PageHeader
        title="Toxicity Management"
        subtitle="CTCAE grading, adverse event tracking, and dose modification recommendations"
        icon={<ToxicityIcon />}
        backTo="/oncology"
        actions={
          <HeaderButton
            startIcon={<AddIcon />}
            onClick={() => navigate('/oncology/toxicity/new')}
          >
            New Record
          </HeaderButton>
        }
      />

      <ContentCard>
        <CardContent>
          {/* Search and Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search by patient name or adverse event..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              sx={{ minWidth: 280 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Grade</InputLabel>
              <Select
                value={gradeFilter}
                label="Grade"
                onChange={(e) => setGradeFilter(e.target.value)}
              >
                <MenuItem value="">All Grades</MenuItem>
                {CTCAE_GRADES.filter(g => g !== '0').map(grade => (
                  <MenuItem key={grade} value={grade}>Grade {grade}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="">All Categories</MenuItem>
                {TOXICITY_CATEGORIES.map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button variant="contained" size="small" onClick={handleSearch}>
              Search
            </Button>

            {(searchTerm || gradeFilter || categoryFilter) && (
              <Button size="small" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Results Table */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : records.length === 0 ? (
            <Alert severity="info">
              No toxicity records found. Use the filters above to search or click "New Record" to add one.
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Patient</TableCell>
                      <TableCell>Adverse Event</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="center">Grade</TableCell>
                      <TableCell>Onset Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Causative Agent</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {records.map((record) => (
                      <TableRow 
                        key={record.id} 
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleViewRecord(record)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {record.patientName || 'Unknown Patient'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {record.patientMRN || ''}
                          </Typography>
                        </TableCell>
                        <TableCell>{record.adverseEvent}</TableCell>
                        <TableCell>
                          <Chip 
                            label={record.category} 
                            size="small" 
                            variant="outlined"
                            sx={{ fontSize: '0.75rem' }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`G${record.ctcaeGrade}`}
                            size="small"
                            color={getGradeColor(record.ctcaeGrade)}
                          />
                        </TableCell>
                        <TableCell>{formatDate(record.onsetDate)}</TableCell>
                        <TableCell>
                          <Chip
                            label={record.resolvedDate ? 'Resolved' : 'Active'}
                            size="small"
                            color={record.resolvedDate ? 'default' : 'warning'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{record.causativeAgent || '-'}</TableCell>
                        <TableCell align="center">
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewRecord(record);
                            }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50]}
              />
            </>
          )}
        </CardContent>
      </ContentCard>
    </Box>
  );
};

export default ToxicityList;
